import { ContextRegistry, useDynamicViewStore } from '@supply-mover-2-0/core';
import {
	ConversationErrorSeverity,
	type ConversationErrorModel,
	type DynamicViewConfigModel,
	type NavigationEvent as ServerNavigationEvent,
} from '@supply-mover-2-0/shared';
// Import AIRegistry by RELATIVE path, NOT the '@supply-mover-2-0/web-ui' barrel.
// A self-import of the package barrel pulls the ENTIRE UI library (the icon
// picker's full FontAwesome sets, list-builder, the MUI email builder, …) into
// the agent widget's bundle and defeats the lean embed entry.
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { AIRegistry } from '../../services/AIRegistry';
import { NavigationEventService } from '../../services/NavigationEventService';
import { Toast } from '../../services/ToastService';
import type { AgentTransport } from '../transport/AgentTransport';
import { AudioUrlPlayer } from './audio/AudioUrlPlayer';
import { MicrophoneService } from './audio/MicrophoneService';
import {
	AlignmentService,
	type AlignmentUpdateData,
} from './messaging/AlignmentService';
import { MessageService } from './messaging/MessageService';
import { AudioStreamingService } from './orchestration/AudioStreamingService';

type EventHandler = (...args: any[]) => void;
type EventType =
	| 'voice-stream-start'
	| 'voice-stream-chunk'
	| 'voice-stream-end'
	| 'voice-stream-abort'
	| 'voice-url'
	| 'transcription'
	| 'alignment-update'
	| 'message'
	| 'action'
	| 'playback-start'
	| 'playback-complete'
	| 'recording-started'
	| 'recording-ended'
	| 'microphone-lost'
	| 'error'
	| 'permission-granted'
	| 'call-ended'
	| 'session-joined'
	| 'streaming-complete'
	| 'streaming-text'
	| 'voice-stream-error'
	| 'action-executed'
	| 'stream-error'
	| 'tool-result'
	| 'tool-started'
	| 'tool-completed'
	| 'tool-usage-ready'
	| 'conversation-error';

/**
 * Represents an active tool execution being tracked
 */
interface ActiveToolInfo {
	toolJobId: string;
	toolName: string;
	description: string;
	startedAt: Date;
	toolUsageId?: number; // Set when tool-usage-ready event arrives
}

interface AiAgentHandle {
	ready: Promise<void>;
	release: () => void;
}

interface AcquireOptions {
	initialCommunicationMode?: 'voice' | 'chat';
	skipMicrophonePermissions?: boolean;
	inputValues?: Record<string, any>;
	/** For system agents — pass instead of agentId to let the gateway resolve the real ID */
	systemAgentType?: string;
	/**
	 * How to reach the server (base URL + socket auth), supplied by the host so
	 * the manager doesn't depend on crm-web/core singletons. Required to connect.
	 */
	transport?: AgentTransport;
}

interface AiAgentState {
	isConnected: boolean;
	isConnecting: boolean;
	isApproved: boolean; // Whether backend has approved the call (different from WebSocket connected)
	isRecording: boolean;
	isPlayingAudio: boolean;
	isMuted: boolean;
	isCallEnded: boolean;
	messages: any[];
	currentStreamingText: string;
	currentTranscript: string;
	alignmentText: string;
	alignmentCharIndex: number;
	micAudioLevel: number;
	agentAudioLevel: number;
	error: string | null;
	connectionError: string | null;
	permissionStatus: 'checking' | 'granted' | 'denied' | 'error';
	isPermanentBlock: boolean;
	communicationMode: 'voice' | 'chat';
	conversationError: ConversationErrorModel | null;
	isFatalError: boolean;
	isWaitingInQueue: boolean;
	queuePosition: number | null;
	activeCallCount: number | null;
	estimatedWaitSeconds: number | null;
	activeTools: Map<string, ActiveToolInfo>; // Currently executing tools (toolJobId -> info)
	isThinking: boolean; // Derived from activeTools.size > 0
	isProcessing: boolean; // True from when user sends message until first response arrives (covers LLM thinking time)
}

/**
 * AI Agent Manager - Hoisted outside React lifecycle
 * This singleton manages all AI agent connections and services,
 * preventing duplicate connections in React StrictMode
 */
class AiAgentManager {
	// Services
	private socket: Socket | null = null;
	private microphoneService: MicrophoneService | null = null;
	private audioStreamingService: AudioStreamingService | null = null;
	private audioUrlPlayer: AudioUrlPlayer | null = null;
	private alignmentService: AlignmentService | null = null;
	private messageService: MessageService | null = null;

	// Background audio (typing sounds during tool execution)
	private backgroundAudioContext: AudioContext | null = null;
	private backgroundGainNode: GainNode | null = null;
	private backgroundNextStartTime: number = 0;

	// Connection management
	private refCount = 0;
	private currentAgentId: number | null = null;
	private currentInputValues: Record<string, any> | undefined = undefined;
	private currentSystemAgentType: string | undefined = undefined;
	// Monotonic sequence for live tool-usage ids. `Date.now()` alone collides when
	// tools finish in the same millisecond (parallel/fast calls), and the tool
	// activity view dedupes by id — so colliding ids made some tool calls silently
	// never surface. Combining the timestamp with this counter keeps ids unique
	// (and roughly time-ordered) within a session.
	private liveToolUsageSeq = 0;
	private currentTransport: AgentTransport | undefined = undefined;
	private connectionPromise: Promise<void> | null = null;
	private activeStreamId: string | null = null;
	private voiceStreamEnded: boolean = false; // Track if voice stream has ended (but audio may still be playing)
	private isAborting: boolean = false; // Track if we're currently aborting (to prevent playback-complete from firing)
	// Mute tracking
	private muteTimestamp: number | null = null;
	private muteTimeout: NodeJS.Timeout | null = null;

	// Disconnect debounce to handle React StrictMode
	private disconnectTimeout: NodeJS.Timeout | null = null;
	private readonly DISCONNECT_DELAY = 100; // Small delay to handle StrictMode mount/unmount/mount

	// Separate listener tracking for immediate cleanup
	private webSocketListeners: Array<() => void> = [];
	private serviceListeners: Array<() => void> = [];
	// ContextRegistry subscription cleanup (page context forwarding)
	private contextUnsubscribe: (() => void) | null = null;

	// Deduped event listeners using Sets
	private readonly listeners = new Map<EventType, Set<EventHandler>>();

	// State management
	private state: AiAgentState = {
		isConnected: false,
		isConnecting: false,
		isApproved: false,
		isRecording: false,
		isPlayingAudio: false,
		isMuted: false,
		isCallEnded: false,
		messages: [],
		currentStreamingText: '',
		currentTranscript: '',
		alignmentText: '',
		alignmentCharIndex: 0,
		micAudioLevel: 0,
		agentAudioLevel: 0,
		error: null,
		connectionError: null,
		permissionStatus: 'checking',
		isPermanentBlock: false,
		communicationMode: 'voice',
		conversationError: null,
		isFatalError: false,
		isWaitingInQueue: false,
		queuePosition: null,
		activeCallCount: null,
		estimatedWaitSeconds: null,
		activeTools: new Map<string, ActiveToolInfo>(),
		isThinking: false,
		isProcessing: false,
	};

	// State subscribers for React components
	private readonly stateSubscribers = new Set<(state: AiAgentState) => void>();

	// Audio level monitoring
	private audioLevelInterval: NodeJS.Timeout | null = null;

	// Permission monitoring
	private permissionMonitoringInterval: NodeJS.Timeout | null = null;

	/**
	 * Acquire a reference to the manager
	 * Components call this on mount and release on unmount
	 */
	acquire(agentId: number, options?: AcquireOptions): AiAgentHandle {
		this.refCount++;

		// Store options for later use
		const {
			initialCommunicationMode = 'voice',
			skipMicrophonePermissions = false,
			inputValues,
			systemAgentType,
			transport,
		} = options || {};
		this.currentInputValues = inputValues;
		this.currentSystemAgentType = systemAgentType;
		// Keep the latest non-null transport so reconnects after a bare acquire
		// (e.g. StrictMode remount) still know how to reach the server.
		if (transport != null) {
			this.currentTransport = transport;
		}

		// Check if this is a brand new call (refCount going from 0 to 1)
		const isNewCall = this.refCount === 1;

		// Cancel any pending disconnect (StrictMode unmount/remount)
		if (this.disconnectTimeout) {
			clearTimeout(this.disconnectTimeout);
			this.disconnectTimeout = null;
		}

		// COMPREHENSIVE STATE RESET FOR NEW CALL
		// This ensures fresh state for every user-initiated call
		if (isNewCall) {
			console.debug(
				'[AiAgentManager] New call detected (refCount=1), resetting all state',
			);

			// Reset ALL call-specific state
			this.updateState({
				isCallEnded: false,
				isApproved: false,
				isWaitingInQueue: false,
				queuePosition: null,
				activeCallCount: null,
				estimatedWaitSeconds: null,
				isMuted: false,
				messages: [],
				currentTranscript: '',
				currentStreamingText: '',
				alignmentText: '',
				alignmentCharIndex: 0,
				micAudioLevel: 0,
				agentAudioLevel: 0,
				isPlayingAudio: false,
				communicationMode: initialCommunicationMode,
				conversationError: null,
				isFatalError: false,
				activeTools: new Map<string, ActiveToolInfo>(),
				isThinking: false,
				isProcessing: false,
			});

			// Reset internal state
			this.activeStreamId = null;
			this.voiceStreamEnded = false;
			this.muteTimestamp = null;

			if (this.muteTimeout) {
				clearTimeout(this.muteTimeout);
				this.muteTimeout = null;
			}

			// Reset services for fresh call
			if (this.alignmentService) {
				this.alignmentService.reset();
			}

			if (this.messageService) {
				this.messageService.reset();
			}

			if (this.audioStreamingService) {
				this.audioStreamingService.reset();
			}
		}

		// If same agent and socket still connected, just re-attach listeners
		if (this.currentAgentId === agentId && this.socket?.connected) {
			console.debug(
				'[AiAgentManager] Reusing existing connection for agent',
				agentId,
			);
			this.attachEventListeners();
			return {
				ready: Promise.resolve(),
				release: () => {
					this.handleRelease();
				},
			};
		}

		// If different agent or not connected, establish new connection
		if (this.currentAgentId !== agentId || !this.socket?.connected) {
			// If not already connecting, start new connection
			if (!this.connectionPromise) {
				this.connectionPromise = this.connect(
					agentId,
					skipMicrophonePermissions,
				);
			}
		}

		return {
			ready: this.connectionPromise || Promise.resolve(),
			release: () => {
				this.handleRelease();
			},
		};
	}

	/**
	 * Handle release with immediate listener cleanup and debounced disconnect
	 * Debounce prevents duplicate connections during React StrictMode mount/unmount/mount
	 */
	private handleRelease() {
		this.refCount = Math.max(0, this.refCount - 1);

		if (this.refCount === 0) {
			// IMMEDIATELY cleanup listeners to prevent duplicates
			this.cleanupAllListeners();

			// DEBOUNCE disconnect to handle StrictMode remounting
			this.disconnectTimeout = setTimeout(() => {
				if (this.refCount === 0) {
					this.fullDisconnect();
				}
			}, this.DISCONNECT_DELAY);
		}
	}

	/**
	 * Subscribe to events with deduplication
	 * Multiple subscriptions with same handler are ignored
	 */
	on(event: EventType, handler: EventHandler): () => void {
		let set = this.listeners.get(event);
		if (!set) {
			set = new Set();
			this.listeners.set(event, set);
		}

		// Only add if not already there (prevents duplicates!)
		if (!set.has(handler)) {
			set.add(handler);
		}

		// Return unsubscribe function
		return () => {
			this.off(event, handler);
		};
	}

	off(event: EventType, handler: EventHandler) {
		const set = this.listeners.get(event);
		if (set?.delete(handler)) {
			if (set.size === 0) {
				this.listeners.delete(event);
			}
		}
	}

	/**
	 * Emit event to all registered listeners
	 */
	private emit(event: EventType, ...args: any[]) {
		const set = this.listeners.get(event);
		if (set) {
			set.forEach((handler) => {
				try {
					handler(...args);
				} catch (error) {
					// Silently handle errors in event handlers
				}
			});
		}
	}

	/**
	 * Clean text before adding ellipsis by removing trailing spaces and punctuation
	 */
	private cleanTextForEllipsis(text: string): string {
		// Remove trailing spaces and common punctuation marks
		return text.replace(/[\s.,!?;:]+$/, '');
	}

	/**
	 * Subscribe to state changes
	 */
	subscribeToState(subscriber: (state: AiAgentState) => void): () => void {
		this.stateSubscribers.add(subscriber);
		// Immediately call with current state
		subscriber(this.state);

		return () => {
			this.stateSubscribers.delete(subscriber);
		};
	}

	/**
	 * Update state and notify subscribers
	 */
	private updateState(updates: Partial<AiAgentState>) {
		this.state = { ...this.state, ...updates };
		// Notify all subscribers
		this.stateSubscribers.forEach((sub) => {
			sub(this.state);
		});
	}

	/**
	 * Get current state
	 */
	getState(): AiAgentState {
		return this.state;
	}

	/**
	 * Connect to AI agent
	 */
	private async connect(
		agentId: number,
		skipMicrophonePermissions: boolean = false,
	): Promise<void> {
		// If already connected to same agent, just attach listeners
		if (this.currentAgentId === agentId && this.socket?.connected) {
			this.attachEventListeners();
			return Promise.resolve();
		}

		this.updateState({
			isConnecting: true,
			error: null,
			connectionError: null,
		});

		// If switching to a different agent, reset messages
		if (this.currentAgentId && this.currentAgentId !== agentId) {
			if (this.messageService) {
				this.messageService.reset();
			}
		}

		try {
			// Only check microphone permissions if not skipped (voice mode)
			if (!skipMicrophonePermissions) {
				// Check microphone permissions first with timing detection
				this.updateState({
					permissionStatus: 'checking',
					isPermanentBlock: false,
				});
				const { granted, isPermanentBlock } =
					await this.checkMicrophonePermissionsWithTiming();

				if (!granted) {
					this.updateState({
						permissionStatus: 'denied',
						isPermanentBlock,
					});

					// Start monitoring for permission changes if permanently blocked
					if (isPermanentBlock) {
						this.startPermissionMonitoring();
					}

					throw new Error('Microphone permission denied');
				}

				this.updateState({
					permissionStatus: 'granted',
					isPermanentBlock: false,
				});
			} else {
				// Skip permissions for chat-only mode
				this.updateState({
					permissionStatus: 'granted',
					isPermanentBlock: false,
				});
			}

			// Initialize services
			this.initializeServices();

			// Connect WebSocket
			await this.connectWebSocket(agentId);

			// Attach all event listeners
			this.attachEventListeners();

			// Start microphone capture only if not in chat-only mode
			if (
				!skipMicrophonePermissions &&
				this.state.communicationMode === 'voice'
			) {
				await this.startMicrophoneCapture();
			}

			// Start audio level monitoring
			this.startAudioLevelMonitoring();

			this.currentAgentId = agentId;
			// WebSocket is connected, but not yet approved by backend
			this.updateState({
				isConnected: true,
				isConnecting: false,
				isRecording: true,
			});
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : 'Connection failed';
			const isMicrophoneError =
				error instanceof Error && error.message.includes('permission');
			const isSocketError =
				error instanceof Error &&
				(error.message.includes('WebSocket') ||
					error.message.includes('connection') ||
					error.message.includes('Authentication') ||
					error.message.includes('timeout'));

			this.updateState({
				isConnected: false,
				isConnecting: false,
				// Use connectionError for socket/network failures
				connectionError:
					isSocketError && !isMicrophoneError ? errorMessage : null,
				// Use error for microphone permission failures
				error: isMicrophoneError ? errorMessage : null,
				permissionStatus: isMicrophoneError ? 'denied' : 'error',
			});

			// Clear the connection promise so we can retry
			this.connectionPromise = null;

			throw error;
		}
	}

	/**
	 * Initialize services
	 */
	private initializeServices() {
		// Use singleton instances for all services
		this.microphoneService = MicrophoneService.getInstance();
		this.audioStreamingService = AudioStreamingService.getInstance(); // FIX: Use getInstance
		this.alignmentService = AlignmentService.getInstance();
		this.messageService = MessageService.getInstance();

		// Initialize audio streaming
		this.audioStreamingService.initialize(16000); // 16kHz sample rate for voice

		// Set up alignment service listener for progressive text reveal
		this.alignmentService.on('alignment-update', (data) => {
			// Update message with alignment progress
			const messages = this.state.messages.map((msg) => {
				if (msg.id === data.messageId) {
					// Handle interrupted state
					if (data.isInterrupted) {
						const truncatedText = data.text.substring(0, data.charIndex);
						return {
							...msg,
							content: `${this.cleanTextForEllipsis(truncatedText)}...`, // Truncate to heard portion with ellipsis
							alignedText: undefined,
							alignedCharIndex: undefined,
							isAligning: false,
							wasInterrupted: true,
							metadata: {
								...msg.metadata,
								fullContent: msg.content, // Store full content
								interruptedAt: data.charIndex,
							},
						};
					}
					// Normal alignment update
					return {
						...msg,
						alignedText: data.text.substring(0, data.charIndex),
						alignedCharIndex: data.charIndex,
						isAligning: true,
					};
				}
				return msg;
			});
			this.updateState({
				messages,
				alignmentText: data.text.substring(0, data.charIndex),
				alignmentCharIndex: data.charIndex,
			});
		});

		// Clear alignment state when stopped
		this.alignmentService.on('alignment-stopped', () => {
			// Mark all aligning messages as complete
			const messages = this.state.messages.map((msg) => {
				if (msg.isAligning) {
					return {
						...msg,
						isAligning: false,
					};
				}
				return msg;
			});
			this.updateState({
				messages,
				alignmentText: '',
				alignmentCharIndex: 0,
			});
		});
	}

	/**
	 * Connect WebSocket
	 */
	private async connectWebSocket(agentId: number): Promise<void> {
		// Base URL + auth come from the host-supplied transport, NOT from
		// crm-web/core singletons — that's what keeps the auth/http internals out
		// of the embeddable bundle.
		const transport = this.currentTransport;
		if (transport == null) {
			throw new Error(
				'AiAgentManager.connectWebSocket: no transport configured. acquire() must be called with a transport (provided via AgentTransportProvider).',
			);
		}

		// Get base URL and auth
		const baseUrl = transport.baseUrl.replace(/\/api$/, '');
		const auth = transport.getAuth();

		// Build socket options
		const socketOptions: any = {
			transports: ['websocket', 'polling'],
			reconnection: true,
			reconnectionAttempts: 5,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			timeout: 20000,
			extraHeaders: {},
			auth: {},
			query: {
				// For system agents, pass systemAgentType instead of agentId.
				// The gateway resolves it to a real agentId on the server side.
				...(this.currentSystemAgentType != null
					? { systemAgentType: this.currentSystemAgentType }
					: { agentId }),
				communicationMode: this.state.communicationMode || 'voice',
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				...(this.currentInputValues != null && {
					inputValues: JSON.stringify(this.currentInputValues),
				}),
			},
		};

		// Add authentication. A private `wkd_` key OR a public `wkda_` key both go
		// in `auth.key` (the gateway distinguishes them by prefix); a JWT goes in
		// `auth.token`.
		if (auth != null) {
			if (auth.apiKey != null) {
				socketOptions.auth.key = auth.apiKey;
				socketOptions.extraHeaders['X-API-Key'] = auth.apiKey;
			} else if (auth.bearerToken != null) {
				socketOptions.auth.token = auth.bearerToken;
				socketOptions.extraHeaders['Authorization'] =
					`Bearer ${auth.bearerToken}`;
			}
		}

		// Create socket connection
		const socketUrl = `${baseUrl}/ai-agents`;

		this.socket = io(socketUrl, socketOptions);

		// Wait for connection with auth retry logic
		await new Promise<void>((resolve, reject) => {
			let connectionAttempts = 0;
			const MAX_ATTEMPTS = 2;
			let timeoutHandle: NodeJS.Timeout | null = null;

			const cleanup = () => {
				if (timeoutHandle != null) {
					clearTimeout(timeoutHandle);
					timeoutHandle = null;
				}
			};

			const attemptConnection = () => {
				connectionAttempts++;

				// Set timeout for this attempt
				timeoutHandle = setTimeout(() => {
					reject(new Error('WebSocket connection timeout'));
				}, 10000);

				this.socket!.once('connect', () => {
					cleanup();

					// Send geolocation to server if available
					this.sendGeolocationToServer();

					resolve();
				});

				this.socket!.once('connect_error', async (error) => {
					cleanup();

					// Check if it's an auth error from middleware
					const isAuthError =
						error.message?.includes('Authentication') ||
						error.message?.includes('Unauthorized') ||
						error.message?.includes('authentication required') ||
						error.message?.includes('Authentication required');

					// If auth error and haven't retried yet, ask the HOST transport to
					// re-mint the auth. Only crm-web supplies refreshAuth (it wraps
					// AuthService.refreshAccessToken()); the public embeddable widget
					// omits it because a `wkda_` key is static — there is no token-refresh
					// layer in the agent itself. When absent we fail fast.
					const refreshAuth = transport.refreshAuth;
					if (
						isAuthError &&
						connectionAttempts < MAX_ATTEMPTS &&
						refreshAuth != null
					) {
						console.debug(
							'[AiAgentManager] Socket auth rejected, asking transport to refresh auth...',
						);

						try {
							const newAuth = await refreshAuth();

							if (newAuth != null) {
								console.debug(
									'[AiAgentManager] Auth refreshed, retrying socket connection...',
								);

								if (newAuth.apiKey != null) {
									this.socket!.auth = { key: newAuth.apiKey };
									this.socket!.io.opts.extraHeaders = {
										...this.socket!.io.opts.extraHeaders,
										'X-API-Key': newAuth.apiKey,
									};
								} else if (newAuth.bearerToken != null) {
									this.socket!.auth = { token: newAuth.bearerToken };
									this.socket!.io.opts.extraHeaders = {
										...this.socket!.io.opts.extraHeaders,
										Authorization: `Bearer ${newAuth.bearerToken}`,
									};
								}

								// Reconnect and setup listeners for retry
								this.socket!.connect();
								attemptConnection();

								return;
							}
						} catch (refreshError) {
							console.debug(
								'[AiAgentManager] Transport auth refresh failed:',
								refreshError,
							);
						}
					}

					// Either not an auth error, or refresh failed, or max attempts reached
					reject(error);
				});
			};

			// Start first connection attempt
			attemptConnection();
		});
	}

	/**
	 * Attach all event listeners (WebSocket + Services)
	 */
	private attachEventListeners() {
		if (!this.socket) {
			return;
		}

		// Clean up any existing listeners first
		this.cleanupAllListeners();

		// WebSocket event handlers - track for cleanup
		const wsHandlers: Record<string, (...args: any[]) => void> = {
			connected: (data) => {
				// Update communication mode from server
				if (data.communicationMode) {
					this.updateState({ communicationMode: data.communicationMode });
				}
			},
			'session-joined': (data) => {
				this.handleSessionJoined(data);
			},
			'mode-changed': (data) => {
				this.updateState({ communicationMode: data.mode });
			},
			'stop-audio': (data) => {
				// Server requested to stop audio (e.g., when switching to chat mode)
				if (this.audioStreamingService) {
					this.audioStreamingService.stopAllAudio();
				}
				if (this.audioUrlPlayer) {
					this.audioUrlPlayer.stop();
				}
				this.updateState({ isPlayingAudio: false });
			},
			'streaming-text': (data) => {
				this.handleStreamingText(data);
			},
			'voice-stream-start': (data) => {
				this.handleVoiceStreamStart(data);
			},
			'voice-stream-chunk': (data) => {
				this.handleVoiceStreamChunk(data);
			},
			'voice-stream-end': (data) => {
				this.handleVoiceStreamEnd(data);
			},
			'voice-stream-abort': (data) => {
				this.handleVoiceStreamAbort(data);
			},
			'voice-stream-error': (data) => {
				this.handleVoiceStreamError(data);
			},
			'background-audio-chunk': (data) => {
				this.handleBackgroundAudioChunk(data);
			},
			'voice-url': (data) => {
				this.handleVoiceUrl(data);
			},
			transcription: (data) => {
				this.handleTranscription(data);
			},
			message: (data) => {
				this.handleMessage(data);
			},
			'message-cleanup': (data) => {
				this.handleMessageCleanup(data);
			},
			action: (data) => {
				this.emit('action', data);
			},
			'action-executed': (data) => {
				this.emit('action-executed', data);
			},
			'stream-event': (data) => {
				this.handleStreamEvent(data);
			},
			'stream-error': (error) => {
				this.emit('stream-error', error);
			},
			'stop-capture': (data) => {
				this.handleStopCapture(data);
			},
			'call-ended': (data) => {
				this.handleServerCallEnd(data);
			},
			'end-call': (data) => {
				this.handleServerCallEnd(data);
			},
			error: (error) => {
				this.updateState({ error: error.message || 'WebSocket error' });
				this.emit('error', error);
			},
			'message-chunk': (data) => {
				this.handleMessageChunk(data);
			},
			'message-complete': (data) => {
				this.handleMessageComplete(data);
			},
			'tool-result': (data) => {
				this.handleToolResult(data);
			},
			'conversation-error': (error: ConversationErrorModel) => {
				this.handleConversationError(error);
			},
			'tool-started': (data: {
				toolJobId: string;
				toolName: string;
				messageId?: string;
				description?: string;
				timestamp: string;
			}) => {
				this.handleToolStarted(data);
			},
			'tool-completed': (data: {
				toolJobId: string;
				toolName: string;
				toolUsageId?: number | null;
				messageId?: string;
				success: boolean;
				timestamp: string;
			}) => {
				this.handleToolCompleted(data);
			},
			'tool-usage-ready': (data: {
				toolJobId: string;
				toolUsageId: number;
				toolName: string;
				timestamp: string;
			}) => {
				this.handleToolUsageReady(data);
			},
			'waiting-for-capacity': (data) => {
				this.updateState({
					isApproved: false,
					isWaitingInQueue: true,
					connectionError: data.message || 'Waiting for available agent...',
				});
			},
			'queue-update': (data) => {
				this.updateState({
					queuePosition: data.position ?? null,
					activeCallCount: data.activeCount ?? null,
					estimatedWaitSeconds: data.estimatedWaitSeconds ?? null,
				});
			},
			approved: () => {
				this.updateState({
					isApproved: true,
					isWaitingInQueue: false,
					queuePosition: null,
					activeCallCount: null,
					estimatedWaitSeconds: null,
					connectionError: null,
				});

				// Subscribe to ContextRegistry changes so the agent stays
				// in sync as the user navigates between pages.
				// subscribeToContextChanges() sends the current context immediately
				// on subscribe, so no separate sendCurrentContext() call is needed.
				this.subscribeToContextChanges();
			},
			navigate: (data: ServerNavigationEvent) => {
				this.handleNavigate(data);
			},
			'prompt-update-suggested': (data: {
				newPrompt: string;
				rationale: string;
			}) => {
				this.handlePromptUpdateSuggested(data);
			},
			'first-message-update-suggested': (data: {
				newFirstMessage: string;
				rationale: string;
			}) => {
				this.handleFirstMessageUpdateSuggested(data);
			},
			'agent-name-update-suggested': (data: {
				newName: string;
				rationale: string;
			}) => {
				this.handleAgentNameUpdateSuggested(data);
			},
			'agent-description-update-suggested': (data: {
				newDescription: string;
				rationale: string;
			}) => {
				this.handleAgentDescriptionUpdateSuggested(data);
			},
			'render-view': (data: DynamicViewConfigModel) => {
				this.handleRenderView(data);
			},
			'call-rejected': (data) => {
				// Disconnect first (clears all state including errors)
				this.fullDisconnect();
				// Use reason (not message) since message is the waiting message, not rejection message
				const errorMessage = data.reason || 'Call rejected';
				this.updateState({
					error: errorMessage,
					connectionError: errorMessage,
				});
			},
		};

		// Attach WebSocket listeners and store cleanup functions
		Object.entries(wsHandlers).forEach(([event, handler]) => {
			this.socket!.on(event, handler);
			this.webSocketListeners.push(() => {
				this.socket?.off(event, handler);
			});
		});

		// Service event listeners
		this.attachServiceListeners();
	}

	/**
	 * Attach service event listeners
	 */
	private attachServiceListeners() {
		if (!this.alignmentService || !this.audioStreamingService) {
			return;
		}

		// Alignment service events
		const alignmentHandler = (data: AlignmentUpdateData) => {
			this.updateState({
				alignmentText: data.text,
				alignmentCharIndex: data.charIndex,
			});
			this.emit('alignment-update', data);
		};
		this.alignmentService.on('alignment-update', alignmentHandler);
		this.serviceListeners.push(() => {
			this.alignmentService?.off('alignment-update', alignmentHandler);
		});

		// Audio streaming events
		const playbackStartHandler = () => {
			this.updateState({ isPlayingAudio: true });
			this.emit('playback-start');
		};
		this.audioStreamingService.on('playback-started', playbackStartHandler);
		this.serviceListeners.push(() => {
			this.audioStreamingService?.off('playback-started', playbackStartHandler);
		});

		const playbackStopHandler = () => {
			this.updateState({ isPlayingAudio: false });

			// Don't send playback-complete if we're aborting (interrupt event already sent)
			if (this.isAborting) {
				this.isAborting = false;
				return;
			}

			// Send playback-complete to server if we have an active stream that has ended
			if (
				this.activeStreamId &&
				this.voiceStreamEnded &&
				this.socket?.connected
			) {
				this.socket.emit('playback-complete', {
					messageId: this.activeStreamId,
					timestamp: Date.now(),
				});

				// NOW clear both after sending playback-complete
				this.activeStreamId = null;
				this.voiceStreamEnded = false;
			}

			this.emit('playback-complete');
		};
		this.audioStreamingService.on('playback-stopped', playbackStopHandler);
		this.serviceListeners.push(() => {
			this.audioStreamingService?.off('playback-stopped', playbackStopHandler);
		});
	}

	/**
	 * Clean up ALL event listeners (WebSocket + Services)
	 */
	private cleanupAllListeners() {
		// Clean up WebSocket listeners
		if (this.webSocketListeners.length > 0) {
			this.webSocketListeners.forEach((unsub) => {
				try {
					unsub();
				} catch (error) {
					// Silently handle cleanup errors
				}
			});
			this.webSocketListeners = [];
		}

		// Clean up ContextRegistry subscription
		if (this.contextUnsubscribe != null) {
			this.contextUnsubscribe();
			this.contextUnsubscribe = null;
		}

		// Clean up service listeners
		this.cleanupServiceListeners();
	}

	/**
	 * Clean up service event listeners
	 */
	private cleanupServiceListeners() {
		if (this.serviceListeners.length > 0) {
			this.serviceListeners.forEach((unsub) => {
				try {
					unsub();
				} catch (error) {
					// Silently handle cleanup errors
				}
			});
			this.serviceListeners = [];
		}
	}

	/**
	 * Check microphone permissions
	 */
	/**
	 * Enhanced permission checking with timing to detect permanent blocks
	 */
	private async checkMicrophonePermissionsWithTiming(): Promise<{
		granted: boolean;
		isPermanentBlock: boolean;
	}> {
		const startTime = Date.now();

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			stream.getTracks().forEach((track) => {
				track.stop();
			});
			return { granted: true, isPermanentBlock: false };
		} catch (error) {
			const elapsed = Date.now() - startTime;
			// If denied in < 100ms, browser auto-denied (permanent block)
			// If > 500ms, user manually denied (they had to click)
			const isPermanentBlock = elapsed < 100;

			return { granted: false, isPermanentBlock };
		}
	}

	/**
	 * Start monitoring for permission changes
	 * Polls every 2 seconds to detect when user grants permission in browser settings
	 */
	private startPermissionMonitoring(): void {
		// Clear any existing monitoring
		this.stopPermissionMonitoring();

		this.permissionMonitoringInterval = setInterval(async () => {
			try {
				// Use permissions API if available
				if (navigator.permissions?.query) {
					try {
						const result = await navigator.permissions.query({
							name: 'microphone' as PermissionName,
						});
						if (result.state === 'granted') {
							this.stopPermissionMonitoring();
							// Notify subscribers that permissions are now granted
							this.updateState({
								permissionStatus: 'granted',
								isPermanentBlock: false,
							});
							// Trigger reconnection
							this.emit('permission-granted');
							return;
						}
					} catch (permError) {
						// Fallback if permissions API doesn't support microphone
					}
				}

				// Fallback: try getUserMedia (will fail fast if still blocked)
				const { granted } = await this.checkMicrophonePermissionsWithTiming();
				if (granted) {
					this.stopPermissionMonitoring();
					this.updateState({
						permissionStatus: 'granted',
						isPermanentBlock: false,
					});
					// Trigger reconnection
					this.emit('permission-granted');
				}
			} catch {
				// Keep monitoring
			}
		}, 2000); // Check every 2 seconds
	}

	/**
	 * Stop permission monitoring
	 */
	private stopPermissionMonitoring(): void {
		if (this.permissionMonitoringInterval) {
			clearInterval(this.permissionMonitoringInterval);
			this.permissionMonitoringInterval = null;
		}
	}

	/**
	 * Start microphone capture
	 */
	private async startMicrophoneCapture(): Promise<void> {
		if (!this.microphoneService) {
			return;
		}

		await this.microphoneService.acquire();

		// Setup audio data handler - store as variable for cleanup
		const audioDataHandler = (audioData: Float32Array) => {
			const now = Date.now();

			// If muted AND this audio was captured after muting, send silence
			// This ensures audio captured before muting is still sent
			if (
				this.state.isMuted &&
				this.muteTimestamp &&
				now > this.muteTimestamp + 300
			) {
				// Add 300ms buffer to ensure we capture all audio in the processing pipeline
				// (Audio chunks are 64ms, plus event loop and network delays)
				const emptyData = new Float32Array(audioData.length);
				this.sendAudioToServer(emptyData);
			} else {
				// Send actual audio (even if currently muted but audio is from before)
				this.sendAudioToServer(audioData);
			}
		};

		// Handle microphone loss (permissions revoked, device disconnected) - store for cleanup
		const microphoneLostHandler = (data: {
			reason: string;
			timestamp: number;
		}) => {
			// Emit event for UI to show appropriate message
			this.emit('microphone-lost', data);

			// Treat microphone loss as hang-up
			this.hangUp();
		};

		// Attach handlers
		this.microphoneService.on('audio-data', audioDataHandler);
		this.microphoneService.on('microphone-lost', microphoneLostHandler);

		// Track for cleanup - IMPORTANT: Add to serviceListeners for proper cleanup
		this.serviceListeners.push(() => {
			this.microphoneService?.off('audio-data', audioDataHandler);
		});

		this.serviceListeners.push(() => {
			this.microphoneService?.off('microphone-lost', microphoneLostHandler);
		});

		this.emit('recording-started');
	}

	/**
	 * Send audio data to server
	 */
	private sendAudioToServer(audioData: Float32Array) {
		if (!this.socket?.connected) {
			return;
		}

		// Convert to base64
		const base64Audio = this.float32ArrayToBase64(audioData);

		// Send to server
		this.socket.emit('user-voice', {
			audio: base64Audio,
			sampleRate: 16000,
			timestamp: Date.now(),
		});
	}

	/**
	 * Convert Float32Array to base64
	 */
	private float32ArrayToBase64(float32Array: Float32Array): string {
		const buffer = new ArrayBuffer(float32Array.length * 2);
		const view = new DataView(buffer);

		for (let i = 0; i < float32Array.length; i++) {
			const sample = Math.max(-1, Math.min(1, float32Array[i] ?? 0));
			view.setInt16(i * 2, sample * 0x7fff, true);
		}

		const uint8Array = new Uint8Array(buffer);
		let binary = '';

		for (let i = 0; i < uint8Array.length; i++) {
			binary += String.fromCharCode(uint8Array[i] ?? 0);
		}

		return btoa(binary);
	}

	/**
	 * Start audio level monitoring
	 */
	private startAudioLevelMonitoring() {
		this.audioLevelInterval = setInterval(() => {
			if (this.state.isRecording && this.microphoneService) {
				const micLevel = this.microphoneService.getCurrentAudioLevel();
				this.updateState({ micAudioLevel: micLevel });
			}

			if (this.state.isPlayingAudio && this.audioStreamingService) {
				const agentLevel = this.audioStreamingService.getCurrentAudioLevel();
				this.updateState({ agentAudioLevel: agentLevel });
			}
		}, 50); // Update every 50ms for smooth visualization
	}

	/**
	 * Send user's geolocation to server
	 */
	private sendGeolocationToServer() {
		if (!this.socket?.connected) {
			return;
		}

		// Check if geolocation is available
		if (!navigator.geolocation) {
			return;
		}

		// Request current position
		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords;

				// Send to server
				if (this.socket?.connected) {
					this.socket.emit('updateLocation', {
						latitude,
						longitude,
					});
				}
			},
			(error) => {
				// Silently handle geolocation errors
				// Don't block connection if geolocation fails
			},
			{
				enableHighAccuracy: false,
				timeout: 5000,
				maximumAge: 60000, // Cache for 1 minute
			},
		);
	}

	/**
	 * Handle session joined event
	 */
	private handleSessionJoined(data: any) {
		// Store session ID if provided
		if (data.sessionId) {
			// Could store this for future use if needed
		}

		// Load historical messages if provided
		if (data.messages && Array.isArray(data.messages)) {
			this.updateState({ messages: data.messages });
		}

		this.emit('session-joined', data);
	}

	/**
	 * Send the current ContextRegistry state to the backend via the
	 * dedicated update-context socket event.
	 */
	private sendCurrentContext(): void {
		if (this.socket?.connected !== true) {
			console.debug(
				'[AiAgentManager] sendCurrentContext: socket not connected, skipping',
			);
			return;
		}

		const currentContext = ContextRegistry.getCurrentContext();
		console.debug('[AiAgentManager] sendCurrentContext:', currentContext);
		this.socket.emit('update-context', { context: currentContext });
	}

	/**
	 * Subscribe to ContextRegistry changes and forward them to the backend.
	 * Only the 'page' key is relevant for the agent — other keys are ignored
	 * to avoid noisy updates.
	 * Also sends the current context immediately in case it was already set
	 * before the session started.
	 */
	private subscribeToContextChanges(): void {
		// Clean up any existing subscription first
		if (this.contextUnsubscribe != null) {
			this.contextUnsubscribe();
			this.contextUnsubscribe = null;
		}

		// Track last sent context to avoid duplicate sends
		let lastSentJson: string | null = null;

		const sendIfChanged = (): void => {
			const currentContext = ContextRegistry.getCurrentContext();
			const json = JSON.stringify(currentContext);
			if (json === lastSentJson) {
				return;
			}
			lastSentJson = json;
			this.sendCurrentContext();
		};

		this.contextUnsubscribe = ContextRegistry.subscribe((key: string) => {
			if (key !== 'page') {
				return;
			}
			sendIfChanged();
		});

		// Send current context immediately — handles the case where the user
		// was already on a page before starting the session
		sendIfChanged();
	}

	/**
	 * Handle stream event (for streaming text)
	 */
	private handleStreamEvent(data: any) {
		const event = data.event;
		if (!event) {
			return;
		}

		switch (event.type) {
			case 'conversational':
				if (event.complete) {
					// Complete the assistant message
					const fullText = this.state.currentStreamingText + event.content;

					// Check if we already have a streaming message or need to create one
					let hasStreamingMessage = false;
					const messages = this.state.messages.map((msg) => {
						if (msg.id === 'streaming-message') {
							hasStreamingMessage = true;
							return {
								...msg,
								id: `msg-${Date.now()}-assistant`,
								content: fullText,
								isPending: false,
							};
						}
						return msg;
					});

					// If no streaming message exists, create the final message
					if (!hasStreamingMessage && fullText) {
						messages.push({
							id: `msg-${Date.now()}-assistant`,
							role: 'assistant' as const,
							content: fullText,
							timestamp: new Date(),
							isPending: false,
						});
					}

					this.updateState({
						currentStreamingText: '',
						messages,
					});
					this.emit('streaming-complete', fullText);
				} else {
					// Update streaming text and message
					const currentText = this.state.currentStreamingText + event.content;

					// Update or create streaming message
					let hasStreamingMessage = false;
					const messages = this.state.messages.map((msg) => {
						if (msg.id === 'streaming-message') {
							hasStreamingMessage = true;
							return {
								...msg,
								content: currentText,
							};
						}
						return msg;
					});

					// If no streaming message, create one
					if (!hasStreamingMessage) {
						messages.push({
							id: 'streaming-message',
							role: 'assistant' as const,
							content: currentText,
							timestamp: new Date(),
							isPending: true,
						});
					}

					this.updateState({
						currentStreamingText: currentText,
						messages,
					});
					this.emit('streaming-text', event.content);
				}
				break;
			case 'action':
				this.emit('action', event.content);
				break;
			case 'complete':
				break;
		}
	}

	/**
	 * Handle voice stream error
	 */
	private handleVoiceStreamError(data: any) {
		// Stop audio playback on error
		this.audioStreamingService?.stopAllAudio();
		this.alignmentService?.reset();

		if (this.activeStreamId === data.messageId) {
			this.activeStreamId = null;
			this.voiceStreamEnded = false; // Also reset the flag on error
		}

		this.emit('voice-stream-error', data);
	}

	/**
	 * Handle background audio chunk (typing sounds during tool execution)
	 */
	private async handleBackgroundAudioChunk(data: {
		audio: string;
		format: string;
		type: string;
	}) {
		try {
			// Initialize background audio context if needed
			if (
				this.backgroundAudioContext == null ||
				this.backgroundAudioContext.state === 'closed'
			) {
				// Use 16000 Hz for pcm_16000 format
				const sampleRate = data.format === 'pcm_16000' ? 16000 : 8000;
				this.backgroundAudioContext = new AudioContext({ sampleRate });
				this.backgroundGainNode = this.backgroundAudioContext.createGain();
				this.backgroundGainNode.gain.value = 0.5; // Background sound volume
				this.backgroundGainNode.connect(
					this.backgroundAudioContext.destination,
				);
				this.backgroundNextStartTime = 0;
			}

			// Resume if suspended
			if (this.backgroundAudioContext.state === 'suspended') {
				await this.backgroundAudioContext.resume();
			}

			// Decode base64 audio
			const binaryString = atob(data.audio);
			const bytes = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				bytes[i] = binaryString.charCodeAt(i);
			}

			// Convert 16-bit PCM to float32
			const int16Array = new Int16Array(bytes.buffer);
			const floatData = new Float32Array(int16Array.length);
			for (let i = 0; i < int16Array.length; i++) {
				const sample = int16Array[i];
				floatData[i] = sample != null ? sample / 32768 : 0;
			}

			// Create audio buffer
			const audioBuffer = this.backgroundAudioContext.createBuffer(
				1, // mono
				floatData.length,
				this.backgroundAudioContext.sampleRate,
			);
			audioBuffer.getChannelData(0).set(floatData);

			// Schedule playback
			const source = this.backgroundAudioContext.createBufferSource();
			source.buffer = audioBuffer;
			source.connect(this.backgroundGainNode!);

			const currentTime = this.backgroundAudioContext.currentTime;

			// Reset timing if we've been idle (gap > 100ms means new background audio session)
			if (this.backgroundNextStartTime < currentTime - 0.1) {
				this.backgroundNextStartTime = currentTime;
			}

			const startTime = Math.max(currentTime, this.backgroundNextStartTime);
			source.start(startTime);

			// Update next start time for seamless playback
			this.backgroundNextStartTime = startTime + audioBuffer.duration;
		} catch (error) {
			console.error('Error playing background audio:', error);
		}
	}

	/**
	 * Handle stop capture request from server
	 */
	private handleStopCapture(data: any) {
		// Stop microphone capture if requested
		if (this.microphoneService) {
			this.stopRecording();
		}
	}

	/**
	 * Handle server-initiated call end
	 */
	private handleServerCallEnd(data: any) {
		// Finalize any pending user transcript before server ends call
		if (this.state.currentTranscript && this.state.currentTranscript.trim()) {
			const finalUserMessage = {
				id: `msg-${Date.now()}-voice-final`,
				role: 'user' as const,
				content: this.state.currentTranscript.trim(),
				timestamp: new Date(),
				text: this.state.currentTranscript.trim(), // Keep for compatibility
			};

			// Add the finalized message
			const messages = [...this.state.messages, finalUserMessage];
			this.updateState({
				messages,
				currentTranscript: '', // Clear the transcript
			});
			this.emit('message', finalUserMessage);
		}

		// Mark call as ended in state
		this.updateState({ isCallEnded: true });

		// Emit event for UI to handle
		this.emit('call-ended', {
			reason: data.reason || 'server-initiated',
			serverInitiated: true,
			message: data.message,
			timestamp: data.timestamp || Date.now(),
		});

		// Perform full disconnect but keep messages and isCallEnded state for transcript viewing
		this.fullDisconnect(true, true);
	}

	/**
	 * Handle voice stream start
	 */
	private handleVoiceStreamStart(data: any) {
		// Prevent overlapping streams
		if (this.activeStreamId && this.activeStreamId !== data.messageId) {
			this.handleVoiceStreamEnd(this.activeStreamId);
		}

		this.voiceStreamEnded = false; // Reset flag for new stream
		this.activeStreamId = data.messageId;

		// Create assistant message for this voice stream
		const assistantMessage = {
			id: data.messageId || `msg-${Date.now()}-assistant`,
			role: 'assistant' as const,
			content: '', // Will be updated as chunks arrive
			timestamp: new Date(),
			isPending: true, // Mark as pending until complete
		};

		// Add to messages and clear processing state since agent is responding
		const messages = [...this.state.messages, assistantMessage];
		this.updateState({ messages, isProcessing: false });
		this.emit('message', assistantMessage);

		// Reset alignment for new stream
		this.alignmentService?.reset();

		this.emit('voice-stream-start', data);
	}

	/**
	 * Handle voice stream chunk
	 */
	private handleVoiceStreamChunk(data: any) {
		// Process audio
		if (data.audio) {
			this.audioStreamingService?.addPCMData(data.audio, data.chunkIndex);
		}

		// Process alignment and update message content
		if (data.alignment && data.alignment.chars) {
			// Server sends alignment.chars array, not alignment.text string
			const alignmentData = {
				chars: data.alignment.chars,
				charStartTimesMs: data.alignment.charStartTimesMs || [],
				charDurationsMs: data.alignment.charDurationsMs || [],
			};

			this.alignmentService?.queueAlignment(
				alignmentData,
				data.chunkIndex || 0,
				data.messageId,
			);

			// Update the assistant message with accumulated text from chars array
			const chunkText = data.alignment.chars.join('');
			if (chunkText) {
				const messages = this.state.messages.map((msg) => {
					if (msg.id === data.messageId) {
						// Append new text to existing content
						return {
							...msg,
							content: msg.content + chunkText,
							alignedText: msg.content + chunkText,
						};
					}
					return msg;
				});
				this.updateState({ messages });
			}
		}

		this.emit('voice-stream-chunk', data);
	}

	/**
	 * Handle voice stream end
	 */
	private handleVoiceStreamEnd(data: any) {
		const messageId = typeof data === 'string' ? data : data.messageId;

		// Mark the message as complete (no longer pending)
		const messages = this.state.messages.map((msg) => {
			if (msg.id === messageId) {
				return {
					...msg,
					isPending: false,
				};
			}
			return msg;
		});
		this.updateState({ messages });

		if (this.activeStreamId === messageId) {
			this.voiceStreamEnded = true; // Just set flag, don't clear activeStreamId yet
			// activeStreamId will be cleared after playback completes
		}

		this.emit('voice-stream-end', data);
	}

	/**
	 * Handle voice stream abort
	 */
	private handleVoiceStreamAbort(data: any) {
		// Get current position before stopping
		const alignmentData = this.alignmentService?.getCurrentPosition();
		const textHeard = alignmentData?.textHeard || '';
		const charactersHeard = alignmentData?.charIndex || 0;
		const audioPosition =
			this.audioStreamingService?.getCurrentPlaybackPosition?.() || 0;

		// We know which message we're playing - use activeStreamId
		const messageId = this.activeStreamId;

		if (!messageId) {
			// No active message, nothing to update
			return;
		}

		// Check if this is an "aborted" reason (eager turn cleanup)
		const isAborted = data.reason === 'aborted';

		// If aborted, don't update the message - it will be removed by message-cleanup
		if (!isAborted) {
			// If nothing was heard, remove the empty message entirely
			if (charactersHeard === 0) {
				const messages = this.state.messages.filter(
					(msg) => msg.id !== messageId,
				);
				this.updateState({ messages });
			} else {
				// Update the aborted message to show it was interrupted
				const messages = this.state.messages.map((msg) => {
					if (msg.id === messageId) {
						// Only truncate if we have partial content
						const shouldTruncate =
							charactersHeard > 0 && charactersHeard < msg.content.length;
						return {
							...msg,
							content: shouldTruncate
								? `${this.cleanTextForEllipsis(textHeard)}...`
								: msg.content,
							alignedText: undefined, // Clear alignment
							alignedCharIndex: undefined,
							isAligning: false,
							wasInterrupted: shouldTruncate,
							metadata: {
								...msg.metadata,
								fullContent: shouldTruncate ? msg.content : undefined,
								interruptedAt: shouldTruncate ? charactersHeard : undefined,
								abortReason: data.reason || 'stream-abort',
							},
						};
					}
					return msg;
				});
				this.updateState({ messages });
			}
		}

		// Set aborting flag BEFORE stopping audio to prevent playback-complete from firing
		this.isAborting = true;

		// Stop audio playback
		this.audioStreamingService?.stopAllAudio();

		// Reset alignment
		this.alignmentService?.reset();

		// Only send interrupt acknowledgment for real interruptions, not aborted eager turns
		if (!isAborted) {
			// Send acknowledgment to backend with playback details
			// Use our activeStreamId since we know which message we're playing
			const messageIdToReport = data.messageId || this.activeStreamId;

			if (this.socket?.connected && messageIdToReport) {
				this.socket.emit('interrupt', {
					messageId: messageIdToReport,
					reason: data.reason || 'user-speaking',
					timestamp: Date.now(),
					playbackPosition: audioPosition,
					charactersHeard,
					totalCharacters: alignmentData?.totalChars || 0,
					textHeard,
					percentageHeard: alignmentData?.percentageHeard || 0,
				});
			}
		}

		if (this.activeStreamId === data.messageId) {
			this.activeStreamId = null;
			this.voiceStreamEnded = false; // Also reset the flag on abort
		}

		this.emit('voice-stream-abort', data);
	}

	/**
	 * Handle transcription
	 */
	private handleTranscription(data: any) {
		// Always update current transcript to show preview
		this.updateState({
			currentTranscript: data.text || '',
		});

		this.emit('transcription', data);

		// Create user message when transcription is final
		if (data.isFinal && data.text && data.text.trim()) {
			const userMessage = {
				id: data.messageId || `msg-${Date.now()}-voice`, // Use backend messageId for cleanup tracking
				role: 'user' as const,
				content: data.text,
				timestamp: new Date(),
				text: data.text, // Keep for compatibility
			};

			// Add to messages, clear transcript preview, and signal we're waiting for a response
			const messages = [...this.state.messages, userMessage];
			this.updateState({
				messages,
				currentTranscript: '', // Clear preview when final
				isProcessing: true,
			});
			this.emit('message', userMessage);
		}
	}

	/**
	 * Handle streaming text in chat mode
	 */
	private handleStreamingText(data: any) {
		// Update current streaming text and clear processing state since we have a response
		const currentText = this.state.currentStreamingText + (data.content || '');
		this.updateState({
			currentStreamingText: currentText,
			isProcessing: false,
		});

		this.emit('streaming-text', data);
	}

	/**
	 * Handle voice URL - play audio from a pre-generated URL
	 */
	private async handleVoiceUrl(data: {
		messageId: string;
		voiceUrl: string;
		duration?: number;
		alignmentData?: any;
	}) {
		try {
			// Stop any current streaming audio
			if (this.audioStreamingService) {
				await this.audioStreamingService.stop();
			}

			// Initialize URL player if needed
			if (!this.audioUrlPlayer) {
				this.audioUrlPlayer = new AudioUrlPlayer();

				// Set up event handlers
				this.audioUrlPlayer.on('playback-started', () => {
					this.updateState({ isPlayingAudio: true });
					this.emit('playback-start');
				});

				this.audioUrlPlayer.on('playback-complete', () => {
					this.updateState({ isPlayingAudio: false });
					this.emit('playback-complete');

					// Don't send playback-complete if we're aborting
					if (!this.isAborting) {
						this.socket?.emit('playback-complete', {
							messageId: data.messageId,
							playbackType: 'url',
						});
					} else {
						this.isAborting = false;
					}

					// Clear activeStreamId and flag after URL playback completes
					if (this.activeStreamId === data.messageId) {
						this.activeStreamId = null;
						this.voiceStreamEnded = false;
					}
				});

				this.audioUrlPlayer.on('playback-error', (error) => {
					this.updateState({ isPlayingAudio: false });
				});

				// Handle alignment updates if alignment data is provided
				if (data.alignmentData) {
					this.audioUrlPlayer.on('time-update', (currentTime, duration) => {
						// Calculate character index based on time
						if (this.alignmentService) {
							// This could be enhanced to sync with alignment service
							const progress = currentTime / duration;
							const text = this.alignmentService.getAccumulatedText();
							const charIndex = Math.floor(text.length * progress);

							this.emit('alignment-update', {
								messageId: data.messageId,
								alignedCharIndex: charIndex,
								alignedText: text.substring(0, charIndex),
							});
						}
					});
				}
			}

			// Update message with voice URL and clear processing state
			const messages = this.state.messages.map((msg) => {
				if (msg.id === data.messageId) {
					return { ...msg, voiceUrl: data.voiceUrl };
				}
				return msg;
			});
			this.updateState({ messages, isProcessing: false });

			// Play the audio
			await this.audioUrlPlayer.play(data.voiceUrl);
			this.activeStreamId = data.messageId;
			this.voiceStreamEnded = true; // For URL playback, stream is already "ended" (pre-recorded)

			// Emit event for UI
			this.emit('voice-url', data);
		} catch (error) {
			this.updateState({ isPlayingAudio: false });
		}
	}

	/**
	 * Handle message
	 */
	private handleMessage(data: any) {
		// Normalize ID - server sends messageId, client uses id
		const messageId = data.messageId ?? data.id;

		// Check if message already exists (by ID)
		const existingMessage = this.state.messages.find(
			(msg) => msg.id === messageId,
		);

		// If this is an interrupted message due to mode change, stop any ongoing playback
		if (data.wasInterrupted && data.reason === 'mode-change-to-chat') {
			// Immediately stop any audio that might still be playing
			if (this.audioStreamingService) {
				this.audioStreamingService.stopAllAudio();
			}

			if (this.audioUrlPlayer) {
				this.audioUrlPlayer.stop();
			}

			this.alignmentService?.reset();
			this.updateState({ isPlayingAudio: false });
		}

		let messages;

		if (existingMessage != null) {
			// Update existing message with the full content
			messages = this.state.messages.map((msg) =>
				msg.id === messageId
					? {
							...msg,
							...data,
							id: messageId,
							// Ensure we show the full content for interrupted messages
							content: data.content,
							alignedText: data.content,
							wasInterrupted: data.wasInterrupted,
						}
					: msg,
			);
		} else {
			// Add new message to history
			// Ensure timestamp is set when message is first received (not at render time)
			messages = [
				...this.state.messages,
				{ ...data, id: messageId, timestamp: data.timestamp ?? new Date() },
			];

			if (messages.length > 100) {
				messages.shift(); // Keep max 100 messages
			}
		}

		this.updateState({ messages });
		this.emit('message', data);
	}

	private handleMessageCleanup(data: { messageIds: Array<string> }) {
		const messages = this.state.messages.filter(
			(msg) => !data.messageIds.includes(msg.id),
		);

		this.updateState({ messages });
	}

	/**
	 * Handle message chunk (chat mode streaming)
	 * Accumulates chunks and updates the streaming assistant message
	 */
	private handleMessageChunk(data: { text: string; messageId: string }) {
		const { text, messageId } = data;

		// Find existing streaming message or create new one
		const messages = [...this.state.messages];
		const existingIndex = messages.findIndex(
			(m) => m.id === messageId || m.id === 'streaming-message',
		);

		if (existingIndex >= 0) {
			// Append to existing message
			messages[existingIndex] = {
				...messages[existingIndex],
				id: messageId,
				content: messages[existingIndex].content + text,
			};
		} else {
			// Create new streaming message
			messages.push({
				id: messageId,
				role: 'assistant' as const,
				content: text,
				timestamp: new Date(),
				direction: 'incoming' as const,
			});
		}

		// Clear processing state since we have a response
		this.updateState({ messages, isProcessing: false });
	}

	/**
	 * Handle message complete (chat mode)
	 * Finalizes the streaming message with the complete content
	 */
	private handleMessageComplete(data: { messageId: string; content: string }) {
		const messages = [...this.state.messages];
		const idx = messages.findIndex(
			(m) => m.id === data.messageId || m.id === 'streaming-message',
		);

		if (idx >= 0) {
			messages[idx] = {
				...messages[idx],
				id: data.messageId,
				content: data.content,
			};
			this.updateState({ messages, isProcessing: false });
		}
	}

	/**
	 * Handle tool result (live tool execution display)
	 * Attaches tool usage data to the originating message for display
	 */
	private handleToolResult(data: {
		messageId: string;
		toolName: string;
		toolType?: string;
		status: 'success' | 'error';
		inputData?: Record<string, unknown>;
		outputData?: unknown;
		errorMessage?: string;
		entityReferences?: Array<{
			entityType: string;
			entityId: number;
			relationshipType: string;
		}>;
	}) {
		const messages = this.state.messages.map((msg) => {
			if (msg.id !== data.messageId) {
				return msg;
			}

			const existingToolUsage = msg.metadata?.toolUsage ?? [];
			const toolUsage = {
				// Unique even when tools complete in the same millisecond — a bare
				// Date.now() collided and the id-deduped activity view dropped the
				// duplicates, so some parallel tool calls never surfaced.
				id: Date.now() * 1000 + (this.liveToolUsageSeq++ % 1000),
				toolName: data.toolName,
				toolType: data.toolType,
				status: data.status,
				inputData: data.inputData,
				outputData: data.outputData,
				errorMessage: data.errorMessage,
				entityReferences: data.entityReferences ?? [],
				createdDate: new Date().toISOString(),
			};

			return {
				...msg,
				metadata: {
					...msg.metadata,
					toolUsage: [...existingToolUsage, toolUsage],
				},
			};
		});

		this.updateState({ messages });
		this.emit('tool-result', data);

		// Dispatch data-changed events for write operations so CRM pages can refresh.
		// Only for the sales-helper system agent — other agents don't modify CRM data
		// that the user is actively viewing.
		if (
			typeof window !== 'undefined' &&
			this.currentSystemAgentType === 'sales-helper' &&
			data.status === 'success' &&
			data.entityReferences != null
		) {
			for (const ref of data.entityReferences) {
				if (
					ref.relationshipType === 'created' ||
					ref.relationshipType === 'modified' ||
					ref.relationshipType === 'deleted'
				) {
					window.dispatchEvent(
						new CustomEvent('agent-data-changed', {
							detail: {
								entityType: ref.entityType,
								entityId: ref.entityId,
								action: ref.relationshipType,
								toolName: data.toolName,
							},
						}),
					);
				}
			}
		}
	}

	/**
	 * Handle conversation error from backend
	 * Fatal errors end the conversation and show error inline in chat
	 * Warning errors show a toast notification
	 */
	private handleConversationError(error: ConversationErrorModel) {
		// Log detailed error to console for debugging
		console.error(`[AI Agent Error] ${error.code}: ${error.debugMessage}`);

		const isFatal = error.severity === ConversationErrorSeverity.FATAL;

		this.updateState({
			conversationError: error,
			isFatalError: isFatal,
		});

		if (isFatal) {
			// Add error as a system message to chat history
			this.addSystemMessage({
				type: 'error',
				content: error.userMessage,
			});

			// Stop recording/playback
			if (this.microphoneService) {
				this.microphoneService.release();
			}

			if (this.audioStreamingService) {
				this.audioStreamingService.stopAllAudio();
			}

			this.updateState({
				isRecording: false,
				isPlayingAudio: false,
				isCallEnded: true,
			});
		} else {
			// Non-fatal warning: show toast notification
			// Check if this warning should only show in dev/staging
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const isProduction = (import.meta as any).env?.MODE === 'production';
			const shouldShow = error.devOnly !== true || !isProduction;

			if (shouldShow) {
				Toast.warning(error.userMessage);
			}
		}

		this.emit('conversation-error', error);
	}

	/**
	 * Handle tool-started event from server
	 * Adds tool to activeTools map and updates isThinking state
	 */
	private handleToolStarted(data: {
		toolJobId: string;
		toolName: string;
		messageId?: string;
		description?: string;
		timestamp: string;
	}): void {
		const toolInfo: ActiveToolInfo = {
			toolJobId: data.toolJobId,
			toolName: data.toolName,
			description: data.description ?? `Executing ${data.toolName}...`,
			startedAt: new Date(data.timestamp),
		};

		// Create a new Map with the added tool
		const activeTools = new Map(this.state.activeTools);
		activeTools.set(data.toolJobId, toolInfo);

		this.updateState({
			activeTools,
			isThinking: true, // Always true when we have active tools
			isProcessing: false, // Tools provide their own visual feedback via ThinkingIndicator
		});

		this.emit('tool-started', data);
	}

	/**
	 * Handle tool-completed event from server
	 * Removes tool from activeTools map and updates isThinking state
	 */
	private handleToolCompleted(data: {
		toolJobId: string;
		toolName: string;
		toolUsageId?: number | null;
		messageId?: string;
		success: boolean;
		timestamp: string;
	}): void {
		// Create a new Map without the completed tool
		const activeTools = new Map(this.state.activeTools);
		activeTools.delete(data.toolJobId);

		this.updateState({
			activeTools,
			isThinking: activeTools.size > 0, // Only thinking if tools remain
		});

		this.emit('tool-completed', data);
	}

	/**
	 * Handle tool-usage-ready event from server
	 * Called when the queue handler has created the tool usage record in DB
	 * Updates the activeTools map with the toolUsageId
	 */
	private handleToolUsageReady(data: {
		toolJobId: string;
		toolUsageId: number;
		toolName: string;
		timestamp: string;
	}): void {
		// Update the tool's toolUsageId in activeTools
		const existingTool = this.state.activeTools.get(data.toolJobId);

		if (existingTool != null) {
			const activeTools = new Map(this.state.activeTools);

			activeTools.set(data.toolJobId, {
				...existingTool,
				toolUsageId: data.toolUsageId,
			});

			this.updateState({ activeTools });
		}

		this.emit('tool-usage-ready', data);
	}

	/**
	 * Handle navigate event from server (AI agent navigation tool)
	 * Navigates the user to a page in the application
	 */
	private handleNavigate(data: ServerNavigationEvent): void {
		const { url, external } = data;

		if (external) {
			// Open in new tab
			if (typeof window !== 'undefined') {
				window.open(url, '_blank', 'noopener,noreferrer');
			}
		} else {
			// Internal navigation using NavigationEventService
			NavigationEventService.navigateTo(url);
		}

		// Show a toast notification to confirm navigation
		const action = data.action ?? 'page';
		if (external) {
			Toast.info(`Opened ${action} in new tab`);
		}
	}

	/**
	 * Prompt-update suggestions from the agent-architect system agent.
	 *
	 * The architect proposes full-replace edits to the agent being edited
	 * via the Update Prompt tool on the server. The tool emits a socket
	 * event; we fan it out to listeners (the editor component) which
	 * render an accept/reject diff card. Nothing is applied until the
	 * user accepts.
	 */
	private promptUpdateListeners = new Set<
		(payload: { newPrompt: string; rationale: string }) => void
	>();

	public onPromptUpdateSuggested(
		listener: (payload: { newPrompt: string; rationale: string }) => void,
	): () => void {
		this.promptUpdateListeners.add(listener);
		return () => {
			this.promptUpdateListeners.delete(listener);
		};
	}

	private handlePromptUpdateSuggested(data: {
		newPrompt: string;
		rationale: string;
	}): void {
		console.debug(
			`[AiAgentManager] Prompt update suggested (${data.newPrompt.length} chars): ${data.rationale}`,
		);
		for (const listener of this.promptUpdateListeners) {
			listener(data);
		}
	}

	/**
	 * First-message-update suggestions from the agent-architect.
	 * Same accept/reject pattern as prompt updates — different field.
	 */
	private firstMessageUpdateListeners = new Set<
		(payload: { newFirstMessage: string; rationale: string }) => void
	>();

	public onFirstMessageUpdateSuggested(
		listener: (payload: { newFirstMessage: string; rationale: string }) => void,
	): () => void {
		this.firstMessageUpdateListeners.add(listener);
		return () => {
			this.firstMessageUpdateListeners.delete(listener);
		};
	}

	private handleFirstMessageUpdateSuggested(data: {
		newFirstMessage: string;
		rationale: string;
	}): void {
		console.debug(
			`[AiAgentManager] First-message update suggested (${data.newFirstMessage.length} chars): ${data.rationale}`,
		);
		for (const listener of this.firstMessageUpdateListeners) {
			listener(data);
		}
	}

	/**
	 * Agent-name-update suggestions from the agent-architect.
	 * Same accept/reject pattern as prompt updates — different field.
	 */
	private agentNameUpdateListeners = new Set<
		(payload: { newName: string; rationale: string }) => void
	>();

	public onAgentNameUpdateSuggested(
		listener: (payload: { newName: string; rationale: string }) => void,
	): () => void {
		this.agentNameUpdateListeners.add(listener);
		return () => {
			this.agentNameUpdateListeners.delete(listener);
		};
	}

	private handleAgentNameUpdateSuggested(data: {
		newName: string;
		rationale: string;
	}): void {
		console.debug(
			`[AiAgentManager] Agent name update suggested: "${data.newName}" — ${data.rationale}`,
		);
		for (const listener of this.agentNameUpdateListeners) {
			listener(data);
		}
	}

	/**
	 * Agent-description-update suggestions from the agent-architect.
	 */
	private agentDescriptionUpdateListeners = new Set<
		(payload: { newDescription: string; rationale: string }) => void
	>();

	public onAgentDescriptionUpdateSuggested(
		listener: (payload: { newDescription: string; rationale: string }) => void,
	): () => void {
		this.agentDescriptionUpdateListeners.add(listener);
		return () => {
			this.agentDescriptionUpdateListeners.delete(listener);
		};
	}

	private handleAgentDescriptionUpdateSuggested(data: {
		newDescription: string;
		rationale: string;
	}): void {
		console.debug(
			`[AiAgentManager] Agent description update suggested (${data.newDescription.length} chars): ${data.rationale}`,
		);
		for (const listener of this.agentDescriptionUpdateListeners) {
			listener(data);
		}
	}

	/**
	 * Handle render-view event from the agent.
	 * Stores the view config in Zustand and navigates to the dynamic view page.
	 */
	private handleRenderView(data: DynamicViewConfigModel): void {
		useDynamicViewStore.getState().setCurrentView(data);
		NavigationEventService.navigateTo('/ai-agents/dynamic-view');
		Toast.info(`Rendering: ${data.title}`);
	}

	/**
	 * Add a system message to the chat history
	 * Used for error messages and other system notifications
	 */
	private addSystemMessage(msg: {
		type: 'error' | 'info';
		content: string;
	}): void {
		const systemMessage = {
			id: `system-${Date.now()}`,
			role: 'system',
			content: msg.content,
			timestamp: Date.now(),
			direction: 'incoming' as const,
			metadata: {
				messageType: msg.type,
			},
		};

		this.updateState({
			messages: [...this.state.messages, systemMessage],
		});
	}

	/**
	 * Interrupt current playback
	 */
	private interruptPlayback(reason: string = 'user-speaking') {
		if (!this.socket?.connected) {
			return;
		}

		// Use activeStreamId directly - it now remains set until playback completes
		const messageId = this.activeStreamId;

		if (!messageId) {
			return;
		}

		// Get detailed playback position if available
		const alignmentData = this.alignmentService?.getCurrentPosition();
		const audioPosition =
			this.audioStreamingService?.getCurrentPlaybackPosition?.() || 0;

		// Calculate what was actually heard
		const charactersHeard = alignmentData?.charIndex || 0;
		const textHeard = alignmentData?.textHeard || '';

		// If nothing was heard, remove the empty message entirely
		if (charactersHeard === 0) {
			const messages = this.state.messages.filter(
				(msg) => msg.id !== messageId,
			);
			this.updateState({ messages });
		} else if (charactersHeard > 0) {
			// Update the interrupted message to show only what was heard
			const messages = this.state.messages.map((msg) => {
				if (msg.id === messageId) {
					return {
						...msg,
						content: `${this.cleanTextForEllipsis(textHeard)}...`, // Truncate to only what was heard with ellipsis
						alignedText: undefined, // Clear alignment
						alignedCharIndex: undefined,
						isAligning: false,
						wasInterrupted: true, // Mark as interrupted
						metadata: {
							...msg.metadata,
							fullContent: msg.content, // Store full content in metadata
							interruptedAt: charactersHeard,
							interruptReason: reason,
						},
					};
				}
				return msg;
			});
			this.updateState({ messages });
		}

		// Send detailed interrupt signal with the correct messageId
		this.socket.emit('interrupt', {
			messageId, // Use the resolved messageId, not just activeStreamId
			reason,
			timestamp: Date.now(),
			playbackPosition: audioPosition,
			charactersHeard,
			totalCharacters: alignmentData?.totalChars || 0,
			textHeard,
			percentageHeard: alignmentData?.percentageHeard || 0,
		});

		// Stop audio locally (both streaming and URL-based)
		this.audioStreamingService?.stopAllAudio();
		this.audioUrlPlayer?.stop();
		this.alignmentService?.stopPlayback(true); // Pass interrupted flag
		this.activeStreamId = null;
	}

	/**
	 * Full disconnect - closes WebSocket and stops all services
	 * @param keepMessages - If true, preserve message history (for hangup scenario)
	 * @param keepCallEnded - If true, preserve isCallEnded state (for server-initiated end)
	 */
	private fullDisconnect(
		keepMessages: boolean = false,
		keepCallEnded: boolean = false,
	) {
		// Clean up all listeners first (should already be done, but just in case)
		this.cleanupAllListeners();

		// Stop permission monitoring
		this.stopPermissionMonitoring();

		// Stop audio level monitoring
		if (this.audioLevelInterval) {
			clearInterval(this.audioLevelInterval);
			this.audioLevelInterval = null;
		}

		// Stop microphone
		if (this.microphoneService) {
			this.microphoneService.release();
			// Don't null - it's a singleton
		}

		// Stop audio playback
		if (this.audioStreamingService) {
			this.audioStreamingService.stopAllAudio();
			this.audioStreamingService.reset();
			// Don't null - it's a singleton
		}

		// Stop URL-based audio playback
		if (this.audioUrlPlayer) {
			this.audioUrlPlayer.destroy();
			this.audioUrlPlayer = null;
		}

		// Stop background audio
		if (this.backgroundAudioContext) {
			this.backgroundAudioContext.close().catch(() => {});
			this.backgroundAudioContext = null;
			this.backgroundGainNode = null;
			this.backgroundNextStartTime = 0;
		}

		// Reset services
		if (this.alignmentService) {
			this.alignmentService.reset();
			// Don't null - it's a singleton
		}

		if (this.messageService && !keepMessages) {
			// Only reset messages if we're not keeping them (e.g., for new agent connection)
			this.messageService.reset();
			// Don't null - it's a singleton
		}

		// Disconnect WebSocket
		if (this.socket) {
			this.socket.removeAllListeners();
			this.socket.disconnect();
			this.socket = null;
		}

		// Clear state
		this.currentAgentId = null;
		this.activeStreamId = null;
		this.voiceStreamEnded = false;
		this.connectionPromise = null;
		this.muteTimestamp = null; // Reset mute timestamp

		// Clear timeouts
		if (this.muteTimeout) {
			clearTimeout(this.muteTimeout);
			this.muteTimeout = null;
		}
		if (this.disconnectTimeout) {
			clearTimeout(this.disconnectTimeout);
			this.disconnectTimeout = null;
		}

		// Build state update, conditionally preserving messages and call ended state.
		// Reset communicationMode to default 'voice' so the next session's component
		// doesn't read a stale mode before acquire() has a chance to set the new one.
		const stateUpdate: Partial<AiAgentState> = {
			isConnected: false,
			isApproved: false,
			isRecording: false,
			isPlayingAudio: false,
			currentStreamingText: '',
			currentTranscript: '',
			alignmentText: '',
			alignmentCharIndex: 0,
			micAudioLevel: 0,
			agentAudioLevel: 0,
			isWaitingInQueue: false,
			queuePosition: null,
			activeCallCount: null,
			estimatedWaitSeconds: null,
			connectionError: null,
			error: null,
			activeTools: new Map<string, ActiveToolInfo>(),
			isThinking: false,
			isProcessing: false,
			communicationMode: 'voice',
		};

		// Only reset isCallEnded if not keeping it (for new sessions)
		if (!keepCallEnded) {
			stateUpdate.isCallEnded = false;
		}

		// Only clear messages if not keeping them
		if (!keepMessages) {
			stateUpdate.messages = [];
		}

		this.updateState(stateUpdate);
	}

	// Public API methods

	async sendMessage(text: string) {
		if (!this.socket?.connected) {
			throw new Error('Not connected');
		}

		// Create user message immediately and add to state
		const userMessage = {
			id: `msg-${Date.now()}-user`,
			role: 'user' as const,
			content: text,
			timestamp: new Date(),
			text, // Keep original text field for compatibility
		};

		// Add user message to state and signal that we're waiting for a response
		const messages = [...this.state.messages, userMessage];
		this.updateState({ messages, isProcessing: true });
		this.emit('message', userMessage);

		// Get available actions from AIRegistry
		const availableActions = AIRegistry.getActions().map((action) => ({
			name: action.name,
			description: action.description,
			category: action.category,
			parameters: action.parameters,
		}));

		// Context is managed separately via the 'update-context' event
		// (fired on navigation and session join), keeping it consistent with the voice path.
		this.socket.emit('send-message', {
			text,
			generateVoice: true,
			autoExecute: true,
			agentId: this.currentAgentId,
			availableActions,
		});
	}

	async setCommunicationMode(mode: 'voice' | 'chat') {
		if (this.state.communicationMode === mode) {
			return;
		}

		// If switching to chat, immediately stop everything BEFORE updating state
		if (mode === 'chat') {
			// Immediately stop all audio
			if (this.audioStreamingService) {
				this.audioStreamingService.stopAllAudio();
			}
			if (this.audioUrlPlayer) {
				this.audioUrlPlayer.stop();
			}

			// Immediately stop microphone using the proper method
			if (this.microphoneService && this.state.isRecording) {
				this.microphoneService.release();
			}

			// Update state to reflect immediate stops
			this.updateState({
				communicationMode: mode,
				isRecording: false,
				isPlayingAudio: false,
			});
		} else {
			// Update state first for voice mode
			this.updateState({ communicationMode: mode });
		}

		// Send mode change to server
		if (this.socket?.connected) {
			this.socket.emit('set-communication-mode', { mode });
		}

		// Handle async operations after immediate stops
		if (mode === 'chat') {
			// Already released above, nothing more to do
		} else if (mode === 'voice') {
			// Re-initialize microphone if switching to voice
			if (this.microphoneService && !this.state.isRecording) {
				try {
					await this.microphoneService.acquire();
					this.updateState({ isRecording: true });
				} catch (error) {
					console.error('Failed to start microphone:', error);
				}
			}
		}
	}

	getCommunicationMode() {
		return this.state.communicationMode;
	}

	async startRecording() {
		if (!this.microphoneService) {
			return;
		}

		// Already handled in connection
		this.updateState({ isRecording: true });
		this.emit('recording-started');
	}

	stopRecording() {
		this.updateState({ isRecording: false });
		this.emit('recording-ended');
	}

	mute() {
		// Track when mute was pressed to avoid cutting off in-flight audio
		this.muteTimestamp = Date.now();
		this.updateState({ isMuted: true });

		// Clear any existing mute timeout (debouncing)
		if (this.muteTimeout) {
			clearTimeout(this.muteTimeout);
			this.muteTimeout = null;
		}

		// Delay notifying server to ensure in-flight audio is processed
		// This gives 300ms for audio to be sent and processed before STT session closes
		this.muteTimeout = setTimeout(() => {
			if (this.socket?.connected) {
				this.socket.emit('user-muted');
			}
			this.muteTimeout = null;
		}, 300);
	}

	unmute() {
		// Clear mute timestamp
		this.muteTimestamp = null;

		// Clear any pending mute timeout (debouncing)
		if (this.muteTimeout) {
			clearTimeout(this.muteTimeout);
			this.muteTimeout = null;
		}

		this.updateState({ isMuted: false });

		// Notify server of unmute state
		if (this.socket?.connected) {
			this.socket.emit('user-unmuted');
		}
	}

	stopAudio() {
		this.audioStreamingService?.stopAllAudio();
		this.audioUrlPlayer?.stop();
		this.alignmentService?.reset();
		// Note: We don't clear activeStreamId here because it's managed by stream events
		// But if someone calls stopAudio directly, they likely want to stop everything
		this.activeStreamId = null;
		this.voiceStreamEnded = false;
	}

	hangUp() {
		// Cancel any pending disconnect timeout
		if (this.disconnectTimeout) {
			clearTimeout(this.disconnectTimeout);
			this.disconnectTimeout = null;
		}

		// Mark call as ended
		this.updateState({ isCallEnded: true });

		// Check if agent is currently speaking and handle interruption
		const streamId = this.activeStreamId;

		// Check both if alignment is active OR if there's an active stream with a pending message
		const hasActiveStream =
			streamId &&
			this.state.messages.some(
				(msg) => msg.id === streamId && (msg.isPending || msg.isAligning),
			);

		if ((this.alignmentService?.isActive() || hasActiveStream) && streamId) {
			// Get current position to know what was actually heard
			const alignmentData = this.alignmentService?.getCurrentPosition();

			if (alignmentData && alignmentData.charIndex > 0) {
				// Update the active message to show only what was heard with ellipsis
				const messages = this.state.messages.map((msg) => {
					if (msg.id === streamId) {
						const truncatedContent = `${this.cleanTextForEllipsis(alignmentData.textHeard)}...`;
						return {
							...msg,
							content: truncatedContent,
							alignedText: undefined,
							alignedCharIndex: undefined,
							isAligning: false,
							wasInterrupted: true,
							isPending: false,
							metadata: {
								...msg.metadata,
								fullContent: msg.content, // Store original full content
								interruptedAt: alignmentData.charIndex,
								interruptReason: 'hang-up',
							},
						};
					}
					return msg;
				});

				this.updateState({ messages });

				// Send interrupt signal to server with all details
				if (this.socket?.connected) {
					this.socket.emit('interrupt', {
						messageId: streamId,
						reason: 'hang-up',
						timestamp: Date.now(),
						playbackPosition: 0, // Time position - we don't track audio time for hang-ups, but server needs this field
						charactersHeard: alignmentData.charIndex, // Character position - this is what's used for truncation
						totalCharacters: alignmentData.totalChars,
						textHeard: alignmentData.textHeard,
						percentageHeard: alignmentData.percentageHeard,
					});
				}

				// Don't call stopPlayback here - it will be handled by stopAudio() later
				// and calling it here causes an alignment-update event that overwrites our truncation
			} else if (hasActiveStream) {
				// If we have an active stream but no alignment position,
				// it might be that audio hasn't started playing yet

				const messages = this.state.messages.map((msg) => {
					if (msg.id === streamId && msg.content) {
						// Take first few words and add ellipsis to show it was cut off at the very beginning
						const words = msg.content.split(' ');
						const truncatedContent = `${this.cleanTextForEllipsis(
							words.slice(0, Math.min(3, words.length)).join(' '),
						)}...`;
						return {
							...msg,
							content: truncatedContent,
							alignedText: undefined,
							alignedCharIndex: undefined,
							isAligning: false,
							wasInterrupted: true,
							isPending: false,
							metadata: {
								...msg.metadata,
								fullContent: msg.content,
								interruptedAt: 0,
								interruptReason: 'hang-up-early',
							},
						};
					}
					return msg;
				});

				this.updateState({ messages });

				// Send interrupt signal for early hang-up too
				if (this.socket?.connected) {
					// Find the message to get its total length
					const activeMessage = messages.find((m) => m.id === streamId);
					const totalChars =
						activeMessage?.metadata?.fullContent?.length ||
						activeMessage?.content?.length ||
						0;

					this.socket.emit('interrupt', {
						messageId: streamId,
						reason: 'hang-up-early',
						timestamp: Date.now(),
						playbackPosition: 0, // Server requires this to trigger handlePlaybackInterrupted
						charactersHeard: 0,
						totalCharacters: totalChars,
						textHeard: '',
						percentageHeard: 0,
					});
				}
			}
		}

		// Finalize any pending user transcript before hanging up
		if (this.state.currentTranscript && this.state.currentTranscript.trim()) {
			const finalUserMessage = {
				id: `msg-${Date.now()}-voice-final`,
				role: 'user' as const,
				content: this.state.currentTranscript.trim(),
				timestamp: new Date(),
				text: this.state.currentTranscript.trim(), // Keep for compatibility
			};

			// Add the finalized message
			const messages = [...this.state.messages, finalUserMessage];
			this.updateState({
				messages,
				currentTranscript: '', // Clear the transcript
			});
			this.emit('message', finalUserMessage);
		}

		// Stop everything AFTER we've captured the interruption state
		this.stopAudio();
		this.stopRecording();

		// Send hang up signal before disconnecting
		if (this.socket?.connected) {
			this.socket.emit('hang-up');
		}

		// Perform full disconnect but keep messages and isCallEnded state for transcript viewing
		this.fullDisconnect(true, true);

		// Clear active stream
		this.activeStreamId = null;
		this.voiceStreamEnded = false;
	}

	forceInterrupt() {
		this.interruptPlayback();
	}

	/**
	 * Reset connection state to allow retry
	 * Called when user wants to retry permission request or connection
	 */
	resetConnection() {
		this.connectionPromise = null;
		this.updateState({
			isConnecting: false,
			error: null,
			connectionError: null,
			isPermanentBlock: false,
		});
		// Stop any existing permission monitoring since we're retrying
		this.stopPermissionMonitoring();
	}
}

// Create ONE instance at module scope - survives HMR
const GLOBAL_KEY = '__AI_AGENT_MANAGER__';
export const aiAgentManager: AiAgentManager =
	(globalThis as any)[GLOBAL_KEY] ??
	((globalThis as any)[GLOBAL_KEY] = new AiAgentManager());

// Keep alive across HMR
if (typeof import.meta !== 'undefined' && (import.meta as any).hot) {
	(import.meta as any).hot.dispose(() => {
		// HMR dispose - keeping manager alive
	});
}
